<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import { userModule } from '@/store/user/module'
import { screenModule } from '@/store/screen/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { quizModule } from '@/store/quiz/module'

const {
    Icon,
} = UIKit

const isLoading = ref(false)
const showCompletedSubjects = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const userFirstName = computed(() => {
    const firstName = userModule.state.user?.firstName
    return firstName && breakpoint?.value !== 'black-bear' ? firstName[0].toUpperCase() + firstName.slice(1) : ''
})

const totalNumOfLevels = computed(() => {
    const subjectsWithLevels = examMetadataModule.state.subjectsWithLevels
    return subjectsWithLevels.reduce((acc, subjectWithLevels) => {
        return acc + subjectWithLevels.levels.length
    }, 0)
})

const levelUpSubjects = computed(() => {
    return examMetadataModule.state.subjectsWithLevels
        .map(s => s.subjectName)
        .sort((a, b) => (a).localeCompare(b, undefined, { numeric: true }))
})

const uniqueAnsweredLevelUpQuestions = computed(() => {
    return quizModule.getters.qetUniqueLevelUpAnsweredQuestionSerials()
})

const numOfDaysToCompleteAllLevels = computed(() => {
    return quizModule.getters.getNumOfDaysToCompleteAllLevels()
})

onMounted(async () => {
    isLoading.value = true

    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userModule.actions.fetchUserData(),
    ])

    isLoading.value = false
})

const toggleCompletedSubjects = () => {
    showCompletedSubjects.value = !showCompletedSubjects.value
}

</script>

<template>
    <div
        v-if="!isLoading"
        v-dark 
        class="recall-challenge-stats-card"
        :class="{ 'recall-challenge-stats-card--showing-subjects': showCompletedSubjects }"
    >
        <div v-dark class="recall-challenge-stats-card__title">
            All levels complete! Way to go {{ userFirstName }}
        </div>
        <div v-dark class="recall-challenge-stats-card__stats">
            <div class="recall-challenge-stats-card__questions-stat">
                <img
                    v-if="isDarkMode"
                    class="recall-challenge-stats-card__questions-icon"
                    src="@/assets/levelup/dark-mode/questions.svg"
                    alt=""
                >
                <img
                    v-else
                    class="recall-challenge-stats-card__questions-icon"
                    src="@/assets/levelup/light-mode/questions.svg"
                    alt=""
                >
                <div v-dark class="recall-challenge-stats-card__num-of-questions">
                    {{ uniqueAnsweredLevelUpQuestions.length }} questions
                </div>
            </div>
            <div class="recall-challenge-stats-card__days-stat">
                <img
                    v-if="isDarkMode"
                    class="recall-challenge-stats-card__calendar-icon"
                    src="@/assets/levelup/dark-mode/calendar.svg"
                    alt=""
                >
                <img
                    v-else
                    class="recall-challenge-stats-card__calendar-icon"
                    src="@/assets/levelup/light-mode/calendar.svg"
                    alt=""
                >
                <div v-dark class="recall-challenge-stats-card__num-of-days">
                    {{ numOfDaysToCompleteAllLevels }} days
                </div>
            </div>
            <div class="recall-challenge-stats-card__levels-stat">
                <img
                    v-if="isDarkMode"
                    class="recall-challenge-stats-card__levels-icon"
                    src="@/assets/levelup/dark-mode/levels.svg"
                    alt=""
                >
                <img
                    v-else
                    class="recall-challenge-stats-card__questions-icon"
                    src="@/assets/levelup/light-mode/levels.svg"
                    alt=""
                >
                <div v-dark class="recall-challenge-stats-card__num-of-levels">
                    {{ totalNumOfLevels }} levels
                </div>
            </div>
        </div>
        <span v-dark class="recall-challenge-stats-card__divider" />
        <div
            v-dark
            class="recall-challenge-stats-card__completed-subjects-toggle"
            :class="{ 'recall-challenge-stats-card__completed-subjects-toggle--open': showCompletedSubjects }"
            @click="toggleCompletedSubjects"
        >
            <div v-dark class="recall-challenge-stats-card__completed-subjects-toggle-text">
                {{ levelUpSubjects.length }} completed subjects
            </div>
            <Icon
                v-dark
                class="recall-challenge-stats-card__completed-subjects-toggle-icon"
                type="accordionArrow" 
            />
        </div>
        <div v-if="showCompletedSubjects" class="recall-challenge-stats-card__subjects">
            <div
                v-for="(levelUpSubject, index) in levelUpSubjects"
                v-dark
                :key="index"
                class="recall-challenge-stats-card__subject"
            >
                <Icon 
                    v-dark 
                    class="recall-challenge-stats-card__check-icon" 
                    type="correct" 
                />
                <div v-dark class="recall-challenge-stats-card__subject-name">
                    {{ levelUpSubject }}
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.recall-challenge-stats-card {
    display: flex;
    flex-direction: column;
    width: 521px;
    height: 180px;
    border-radius: 5px;
    border: 0.5px solid rgba($pewter, 0.85);
    background: $white;

    &--showing-subjects {
        height: fit-content;
    }

    &--dark {
        background: $brand-black;
    }

    @include breakpoint(black-bear) {
        width: 335px;
        height: 208px;

        &--showing-subjects {
            height: fit-content;
        }
    }

    &__title {
        color: $brand-black;
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
        padding: 24px 0 16px 32px;

        &--dark {
            color: $white;
        }

        @include breakpoint(black-bear) {
            padding: 16px 0 12px 14px;
        }
    }

    &__stats {
        display: inline-flex;
        align-items: center;
        gap: 14px;
        margin-left: 32px;
        margin-bottom: 24px;

        @include breakpoint(black-bear) {
            display: inline-flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            gap: 12px;
            margin-left: 16px;
            margin-bottom: 20px;
        }
    }

    &__questions-stat,
    &__days-stat,
    &__levels-stat {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    &__num-of-questions,
    &__num-of-days,
    &__num-of-levels {
        color: $brand-black;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;

        &--dark {
            color: $barely-background;
        }
    }

    &__divider {
        width: 456px;
        border-bottom: 1px solid $fog;
        margin-left: 32px;

        &--dark {
            border-bottom: 1px solid $slate;
        }

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__completed-subjects-toggle {
        width: fit-content;
        display: flex;
        align-items: baseline;

        &--open {
            svg {
                transform: rotate(180deg);
            }
        }

        @include breakpoint(black-bear) {
            width: 100%;
            border-radius: 5px 5px 0 0;
            border-top: 0.5px solid rgba($pewter, 0.85);

            &--open {
                border-bottom: 0.5px solid rgba($pewter, 0.85);
            }
        }
    }

    &__completed-subjects-toggle-text {
        color: $brand-blue;
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        margin: 24px 5px 0 32px;
        cursor: pointer;

        &--dark {
            color: $barely-background;
        }

        @include breakpoint(black-bear) {
            margin: 14px 5px 14px 14px;
        }
    }

    &__completed-subjects-toggle-icon {
        cursor: pointer;
        color: $brand-blue;
        
        &--dark {
            color: $banana-bread;
        }
    }

    &__subjects {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
        padding: 16px 0 26px 32px;

        @include breakpoint(black-bear) {
            padding-left: 16px;
        }
    }

    &__subject {
        display: flex;
    }

    &__check-icon {
        width: 21px;
        height: 22px;
        color: $cadaverous;
        margin-right: 6px;

        &--dark {
            color: $jungle-green
        }
    }

    &__subject-name {
        color: $pickled-bluewood;
        font-size: 15px;
        font-weight: 500;
        line-height: 20px;
        width: 429px;

        &--dark {
            color: $barely-background
        }

        @include breakpoint(black-bear) {
            width: 264px;
        }
    }
}
</style>
